import React, { useContext, createContext, useReducer, useCallback, useMemo } from "react";
import { Fade, Modal } from "@mui/material";
import { Svg } from "assets";

const DispatchContext = createContext(null);

const ACTIONS = {
    OPEN: "MODAL_MAIN_OPEN",
    CLOSE: "MODAL_MAIN_CLOSE",
    CLOSE_ALL: "MODAL_MAIN_CLOSE_ALL",
};

const modalReducer = (stack, action) => {
    switch (action.type) {
        case ACTIONS.OPEN:
            return [...stack, {
                id: action.payload.id,
                content: action.payload.content,
                modalInfo: action.payload.modalInfo,
            }];
        case ACTIONS.CLOSE:
            return stack.length > 0 ? stack.slice(0, -1) : stack;
        case ACTIONS.CLOSE_ALL:
            return stack.length > 0 ? [] : stack;
        default:
            return stack;
    }
};

let modalId = 0;
const generateId = () => {
    modalId += 1;
    return modalId;
};

const BASE_Z_INDEX = 1500;
const Z_INDEX_STEP = 5;

const ModalItem = ({ modal, index, dispatch }) => {
    const { id, content, modalInfo = {} } = modal;
    const {
        noShowCloseBtn,
        dontClose,
        padding,
        maxWidth,
        className,
        styles,
    } = modalInfo;

    const handleBackdropClose = () => {
        if (!dontClose) {
            dispatch({ type: ACTIONS.CLOSE });
        }
    };

    const handleCloseBtn = () => {
        dispatch({ type: ACTIONS.CLOSE });
    };

    return (
        <Modal
            key={id}
            style={{ zIndex: BASE_Z_INDEX + index * Z_INDEX_STEP }}
            open
            onClose={handleBackdropClose}
            closeAfterTransition
            hideBackdrop={false}
        >
            <Fade in>
                <div
                    style={{
                        padding: padding === "noPaddings" ? 0 : "",
                        outline: "none",
                        maxWidth: maxWidth || "inherit",
                        ...styles,
                    }}
                    className={`modal-wrapper ${className || ""}`}
                >
                    {!noShowCloseBtn && (
                        <div className="modal-header-style">
                            <button
                                type="button"
                                className="close-modal-btn"
                                onClick={handleCloseBtn}
                            >
                                <Svg.CloseSvg />
                            </button>
                        </div>
                    )}
                    {content}
                </div>
            </Fade>
        </Modal>
    );
};

export const ModalMainProvider = ({ children }) => {
    const [stack, dispatch] = useReducer(modalReducer, []);

    return (
        <DispatchContext.Provider value={dispatch}>
            {children}
            {stack.map((modal, index) => (
                <ModalItem
                    key={modal.id}
                    modal={modal}
                    index={index}
                    dispatch={dispatch}
                />
            ))}
        </DispatchContext.Provider>
    );
};

export const useModalMain = () => {
    const dispatch = useContext(DispatchContext);

    if (!dispatch) {
        throw new Error("useModalMain must be used within <ModalMainProvider>");
    }

    const openMain = useCallback((content, modalInfo = {}) => {
        dispatch({
            type: ACTIONS.OPEN,
            payload: { id: generateId(), content, modalInfo },
        });
    }, [dispatch]);

    const closeMain = useCallback(() => {
        dispatch({ type: ACTIONS.CLOSE });
    }, [dispatch]);

    const closeAllMain = useCallback(() => {
        dispatch({ type: ACTIONS.CLOSE_ALL });
    }, [dispatch]);

    return useMemo(() => ({ openMain, closeMain, closeAllMain }), [openMain, closeMain, closeAllMain]);
};
